import { Routes } from '@/router/routes'

import { AppPermissions } from '@/types/enums/permissions/permissions'
import { Middleware } from '@/types/enums/middleware/middleware'

import type { RouteLocation } from 'vue-router'

const routeToVehicleOverview = (to: RouteLocation) => ({
  name: Routes.VehicleManagementOverview,
  params: {
    vehicleId: to.params.vehicleId,
  },
})

export const vehiclesRoutes = [
  {
    path: '/vehicles',
    name: Routes.Vehicles,
    component: () => import('@/pages/contexts/portal/vehicles/Index.vue'),
    meta: {
      pageTitle: 'Manage Vehicles',
      middleware: [
        Middleware.REQUIRES_AUTH,
        Middleware.TOGGLES_NAV_DRAWER,
      ],
      permission: AppPermissions.MANAGE_VEHICLES,
    },
    children: [
      {
        path: 'create',
        name: Routes.VehicleCreate,
        component: () => import('@/pages/contexts/portal/vehicles/create/CreateVehiclePage.vue'),
        meta: {
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
      },
    ],
  },
  {
    path: '/vehicles/:vehicleId',
    name: Routes.VehicleManagement,
    component: () => import('@/pages/contexts/portal/vehicles/_id/Index.vue'),
    meta: {
      pageTitle: 'Manage Vehicle',
      middleware: [
        Middleware.REQUIRES_AUTH,
        Middleware.TOGGLES_NAV_DRAWER,
      ],
      permission: AppPermissions.MANAGE_VEHICLES,
    },
    redirect: routeToVehicleOverview,
    children: [
      {
        path: 'overview',
        name: Routes.VehicleManagementOverview,
        component: () => import('@/pages/contexts/portal/vehicles/_id/overview/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Overview',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'edit',
            name: Routes.VehicleUpdate,
            component: () => import('@/pages/contexts/portal/vehicles/_id/overview/edit/UpdateVehiclePage.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
      {
        path: 'image-gallery',
        name: Routes.VehicleManagementImageGallery,
        component: () => import('@/pages/contexts/portal/vehicles/_id/image-gallery/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Image Gallery',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
      },
      {
        path: 'issue-reports',
        name: Routes.VehicleManagementIssueReports,
        component: () => import('@/pages/contexts/portal/vehicles/_id/issue-reports/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Issue Reports',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'create',
            name: Routes.VehicleCreateIssueReport,
            component: () => import('@/pages/contexts/portal/vehicles/_id/issue-reports/create/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
      {
        path: 'cleaning-logs',
        name: Routes.VehicleManagementCleaningLogs,
        component: () => import('@/pages/contexts/portal/vehicles/_id/cleaning-logs/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Cleaning Logs',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'create',
            name: Routes.VehicleCreateCleaningLog,
            component: () => import('@/pages/contexts/portal/vehicles/_id/cleaning-logs/create/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
      {
        path: 'assignments',
        name: Routes.VehicleManagementAssignments,
        component: () => import('@/pages/contexts/portal/vehicles/_id/assignments/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Assignments',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
      },
      {
        path: 'allocations',
        name: Routes.VehicleManagementAllocations,
        component: () => import('@/pages/contexts/portal/vehicles/_id/allocations/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Allocations',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'create',
            name: Routes.VehicleCreateAllocation,
            component: () => import('@/pages/contexts/portal/vehicles/_id/allocations/create/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
      {
        path: 'department-availability',
        name: Routes.VehicleManagementDepartmentAvailabilityLogs,
        component: () => import('@/pages/contexts/portal/vehicles/_id/department-availability-logs/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Operational Status',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'create',
            name: Routes.VehicleCreateDepartmentAvailabilityLog,
            component: () => import('@/pages/contexts/portal/vehicles/_id/department-availability-logs/create/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
      {
        path: 'garage-assignments',
        name: Routes.VehicleManagementGarageAssignmentLogs,
        component: () => import('@/pages/contexts/portal/vehicles/_id/garage-assignment-logs/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Garage Assignments',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'create',
            name: Routes.VehicleCreateGarageAssignmentLog,
            component: () => import('@/pages/contexts/portal/vehicles/_id/garage-assignment-logs/create/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
      {
        path: 'comments',
        name: Routes.VehicleManagementComments,
        component: () => import('@/pages/contexts/portal/vehicles/_id/comments/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Comments',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
      },
      {
        path: 'maintenance-tickets',
        name: Routes.VehicleManagementMaintenanceTickets,
        component: () => import('@/pages/contexts/portal/vehicles/_id/maintenance-tickets/Index.vue'),
        meta: {
          pageTitle: 'Vehicle Maintenance Requests',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_VEHICLES,
        },
        children: [
          {
            path: 'create',
            name: Routes.VehicleManagementCreateMaintenanceTicket,
            component: () => import('@/pages/contexts/portal/vehicles/_id/maintenance-tickets/create/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
          {
            path: 'view/:vehicleMaintenanceTicketId',
            name: Routes.VehicleManagementViewMaintenanceTicket,
            component: () => import('@/pages/contexts/portal/vehicles/_id/maintenance-tickets/view/View.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_VEHICLES,
            },
          },
        ],
      },
    ],
  },
  {
    path: '/vehicles-assigned-to-garage',
    name: Routes.VehiclesAssignedToGarage,
    component: () => import('@/pages/contexts/portal/vehicles-assigned-to-garage/Index.vue'),
    meta: {
      pageTitle: 'Vehicles Assigned to Garage',
      middleware: [
        Middleware.REQUIRES_AUTH,
        Middleware.TOGGLES_NAV_DRAWER,
      ],
      permission: AppPermissions.MANAGE_VEHICLE_MAINTENANCE,
    },
  },
]
